import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { billingRateAPI, projectAPI } from '../services/api';
import api from '../services/api';
import type { BillingRate, Project } from '../types';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
}

const BillingRateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showEmployeeSection, setShowEmployeeSection] = useState(false);
  const [formData, setFormData] = useState<Partial<BillingRate>>({
    project: undefined,
    name: '',
    rate_amount: '',
    description: '',
    is_active: true,
    is_billable: true,
  });

  useEffect(() => {
    fetchProjects();
    if (!isEditMode) {
      fetchUsers();
    }
    if (isEditMode && id) {
      fetchBillingRate(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data.results.filter(p => p.is_active));
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get<{ results: User[] }>('/auth/users/');
      setUsers(response.data.results || response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchBillingRate = async (rateId: number) => {
    try {
      const response = await billingRateAPI.getById(rateId);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to fetch billing rate:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditMode && id) {
        await billingRateAPI.update(parseInt(id), formData);
        navigate('/billing-rates');
      } else {
        // Include selected users for bulk assignment
        const createData = {
          ...formData,
          assign_to_users: selectedUserIds,
        };
        const response = await billingRateAPI.create(createData);
        const assignedCount = response.data.assigned_users_count || 0;
        
        if (assignedCount > 0) {
          alert(`Billing rate created successfully! ${assignedCount} employee(s) were assigned to this rate.`);
        }
        navigate('/billing-rates');
      }
    } catch (error: any) {
      console.error('Failed to save billing rate:', error);
      let errorMsg = 'Failed to save billing rate.';

      if (error.response?.data) {
        if (error.response.data.name) {
          errorMsg = Array.isArray(error.response.data.name)
            ? error.response.data.name.join(' ')
            : error.response.data.name;
        } else if (error.response.data.non_field_errors) {
          errorMsg = error.response.data.non_field_errors.join(' ');
        } else if (error.response.data.detail) {
          errorMsg = error.response.data.detail;
        }
      }

      alert(errorMsg);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' || name === 'project' ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  const handleUserToggle = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const filteredUserIds = filteredUsers.map((u) => u.id);
    setSelectedUserIds((prev) => {
      const allSelected = filteredUserIds.every((id) => prev.includes(id));
      if (allSelected) {
        // Deselect all filtered users
        return prev.filter((id) => !filteredUserIds.includes(id));
      } else {
        // Select all filtered users (merge with existing, no duplicates)
        return Array.from(new Set([...prev, ...filteredUserIds]));
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedUserIds([]);
  };

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    const searchLower = userSearchQuery.toLowerCase();
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return (
      user.username.toLowerCase().includes(searchLower) ||
      fullName.includes(searchLower)
    );
  });

  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedUserIds.includes(u.id));

  return (
    <div className="container">
      <div className="card">
        <h2>{isEditMode ? 'Edit Billing Rate' : 'Create New Billing Rate'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="project">Project *</label>
            <select
              id="project"
              name="project"
              value={formData.project || ''}
              onChange={handleChange}
              required
            >
              <option value="">Select a project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code} - {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="name">Rate Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Senior Developer, Junior Developer"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="rate_amount">Hourly Rate (USD) *</label>
            <input
              type="number"
              id="rate_amount"
              name="rate_amount"
              value={formData.rate_amount}
              onChange={handleChange}
              step="0.01"
              min="0"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ cursor: 'pointer' }}
              />
              <span>Active</span>
            </label>
          </div>

          <div className="form-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_billable"
                checked={formData.is_billable}
                onChange={handleChange}
                style={{ cursor: 'pointer' }}
              />
              <span>Billable</span>
            </label>
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
              Uncheck for overhead, FFP, or internal rates that should not be invoiced
            </small>
          </div>

          {/* Employee Assignment Section - Only shown for new rates */}
          {!isEditMode && (
            <div style={{ marginBottom: '1.5rem' }}>
              <button
                type="button"
                onClick={() => setShowEmployeeSection(!showEmployeeSection)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#3498db',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ 
                  transform: showEmployeeSection ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                }}>
                  ▶
                </span>
                Assign Employees (Optional)
                {selectedUserIds.length > 0 && (
                  <span style={{
                    backgroundColor: '#3498db',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    fontSize: '0.85rem',
                    marginLeft: '0.5rem',
                  }}>
                    {selectedUserIds.length} selected
                  </span>
                )}
              </button>

              {showEmployeeSection && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                }}>
                  <p style={{ 
                    margin: '0 0 1rem 0', 
                    color: '#6c757d', 
                    fontSize: '0.9rem' 
                  }}>
                    Select employees to automatically assign to this billing rate when it's created.
                    You can also manage assignments later from the User Rate Assignments page.
                  </p>

                  {/* Search and controls */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.75rem', 
                    marginBottom: '1rem',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}>
                    <input
                      type="text"
                      placeholder="Search employees..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      style={{
                        flex: '1 1 200px',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}
                    >
                      {allFilteredSelected ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedUserIds.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>

                  {/* Employee list */}
                  <div style={{
                    maxHeight: '250px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                  }}>
                    {filteredUsers.length === 0 ? (
                      <div style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        color: '#6c757d' 
                      }}>
                        {userSearchQuery ? 'No employees match your search' : 'No employees available'}
                      </div>
                    ) : (
                      filteredUsers.map((user) => (
                        <label
                          key={user.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.625rem 0.75rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f3f4',
                            backgroundColor: selectedUserIds.includes(user.id) ? '#e8f4fd' : 'transparent',
                            transition: 'background-color 0.15s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user.id)}
                            onChange={() => handleUserToggle(user.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <div>
                            <div style={{ fontWeight: 500 }}>{user.username}</div>
                            {(user.first_name || user.last_name) && (
                              <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                                {user.first_name} {user.last_name}
                              </div>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update Rate' : 'Create Rate'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/billing-rates')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingRateForm;

